import React, {useState} from 'react';
import styles from '../completed/styles.module.scss';
import {DialogRoot, DialogTrigger, Skeleton, Table} from '@chakra-ui/react';
import Text from 'components/typografy/text';
import {Avatar} from 'components/chakra-ui/avatar';
import {useQuery} from 'react-query';
import {BREAKPOINTS, QUERY_KEYS} from '../../../../constants';
import useWindowSize from 'hooks/useWindowSize';
import {IoChevronForwardOutline} from 'react-icons/io5';
import Dialog from 'components/dialog';
import DialogContent from './dialog-content';
import TabsSkeleton from '../skeleton';
import {ReactComponent as CashIcon} from 'svg/cash2.svg';
import {withdrawalsHistory} from 'api/user';
import {toTitleCase} from 'helpers';
import {ExternalLink} from 'lucide-react';
import {getWithdrawalLink} from 'api/payment';
import { toaster } from 'components/chakra-ui/toaster';

const Withdrawals: React.FC = () => {
  const {width} = useWindowSize();
  const [isOpen, setIsOpen] = useState(false);
  const [activeItem, setActiveItem] = useState<any | null>(); // State to store the currently active item for the dialog (active means item is clicked and dialog is open, mainly mobile)
  const [linkCache, setLinkCache] = useState<Record<string, string>>({});  // Cache for links to avoid multiple requests (a dictionary of withdrawal_id and link, both strings)

  // Use React Query to fetch withdrawls history
  const { data: withdrawals = [], isLoading} = useQuery(
    [QUERY_KEYS.USER.WITHDRAWALS_HISTORY],
     withdrawalsHistory, {
    refetchInterval: 1000 * 10, // Auto-refresh every 10 secs, even if data is fresh
    refetchIntervalInBackground: false,   // Do not poll when browser tab is in the background (even if data is stale)
    staleTime: 1000 * 10,           // Consider fresh for 10 seconds
    refetchOnWindowFocus: true,     // Refetching on tab focus, and data is stale
    refetchOnMount: true,            // Refetching on component mount, and data is stale
    keepPreviousData: true,          // Keep previous data while fetching new data
    retry: (failureCount, error: any) => {
      const status = error?.status;
      // Retry once if it's NOT one of these status codes
      return failureCount < 1 && ![400, 401, 404].includes(status);
    },
    retryDelay: () => 4000, // 4s delay before the single retry
  });

  // Handle withdrawal link click (send to external link)
  const handleLinkClick = (withdrawal_id: string) => {
    if (linkCache[withdrawal_id]) {
      // Already cached, just open it
      window.open(linkCache[withdrawal_id], '_blank');
      return;
    }  
    // Show toast modal "Loading..."
    toaster.create({
      description: 'Loading...',
      duration: 5000,
      type: 'success',
      action: {
        label: 'Close',
        onClick: () => {}
      }
    });
    getWithdrawalLink(withdrawal_id).then((res) => {
      if (res?.link) {
        // Dismiss the toast modal
        toaster.dismiss();
        // Open the link in a new tab
        window.open(res.link, '_blank');
        // Cache the link for future use for successful requests (to avoid multiple requests)
        setLinkCache((prev) => ({ ...prev, [withdrawal_id]: res.link }));
      } 
    })
    .catch((error) => {
      // Avoid unhandled promise rejections (error will be displayed in toast via the interceptor)
    });
  };

  return (
    <DialogRoot
      scrollBehavior="inside"
      size="lg"
      onOpenChange={() => {
        setIsOpen(!isOpen);
      }}
      onCloseChange={() => {
        setIsOpen(!isOpen);
      }}
    >
      <div className={styles.wrapper}>
        <Table.Root size="lg">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>
                <Text variant={'medium'} nativeClassname={styles.white}>
                  Vendor
                </Text>
              </Table.ColumnHeader>
              <Table.ColumnHeader>
                <Text variant={'medium'} nativeClassname={styles.white}>
                  Amount
                </Text>
              </Table.ColumnHeader>
              {width > BREAKPOINTS.LG ? (
                <>
                  <Table.ColumnHeader>
                    <Text variant={'medium'} nativeClassname={styles.white}>
                      Withdrawal ID
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text variant={'medium'} nativeClassname={styles.white}>
                      Withdrawal Time
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text variant={'medium'} nativeClassname={styles.white}>
                      Link
                    </Text>
                  </Table.ColumnHeader>
                  <Table.ColumnHeader>
                    <Text variant={'medium'} nativeClassname={styles.white}>
                      Status
                    </Text>
                  </Table.ColumnHeader>
                </>
              ) : (
                <Table.ColumnHeader></Table.ColumnHeader>
              )}
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {/* Show placeholder of withdrawals history skeleton while data is being fetched for the 1st time */}
            {isLoading ? (
              <TabsSkeleton />
            ) : withdrawals?.length ? (
              withdrawals.map((item: any) => (
                <Table.Row key={item.id}>
                  <Table.Cell>
                    <div className={styles.flex}>
                      {toTitleCase(item.vendor)}
                    </div>
                  </Table.Cell>
                  <Table.Cell>
                    <div className={styles.flex}>
                      <CashIcon />${item.total_amount}
                    </div>
                  </Table.Cell>
                  {width > BREAKPOINTS.LG ? (
                    <>
                      <Table.Cell>{item.id}</Table.Cell>
                      <Table.Cell>{item.created_at}</Table.Cell>
                      <Table.Cell>
                        <div
                            className={styles.ExternalLink}
                            onClick={() => handleLinkClick(item.id)}
                          >
                            <ExternalLink />
                        </div>
                      </Table.Cell>
                      <Table.Cell>Completed</Table.Cell>
                    </>
                  ) : (
                    <Table.Cell textAlign="end">
                      <DialogTrigger>
                        <div
                          className={styles.chevron}
                          onClick={() => {
                            setActiveItem(item);
                          }}
                        >
                          <IoChevronForwardOutline />
                        </div>
                      </DialogTrigger>
                    </Table.Cell>
                  )}
                </Table.Row>
              ))
            ) : (
              <Table.Row>
                <Table.Cell>
                  No withdrawals yet...
                </Table.Cell>
              </Table.Row>
            )}
          </Table.Body>
        </Table.Root>
      </div>
      <Dialog isOpen={isOpen} contentClassNames={styles.zeroPadding} isForgot={false}>
        {activeItem ? <DialogContent data={activeItem} /> : <></>}
      </Dialog>
    </DialogRoot>
  );
};

export default Withdrawals;
